import Link from "next/link";
import type { ComponentProps } from "react";
import { storyblokEditable } from "@storyblok/react/rsc";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { ArrowUpRight } from "@/components/icons/ArrowUpRight";
import { getUrlWithAnchor } from "@/lib/storyblok/utils";

import {
  Button as ButtonComponent,
  type ButtonVariant,
  type ButtonSize,
} from "@/components/ui/button";
import type { ButtonStoryblok } from "@/component-types-sb";
import AuthModalButton from "./AuthModalButton";
import MembershipPaymentButton from "./MembershipPaymentButton";
import PraxisContactButton from "./PraxisContactButton";

// Map Storyblok variant names to ui/button variant names
const variantMap: Record<string, ButtonVariant> = {
  primary: "black",
  secondary: "white",
  sun: "sun",
  black: "black",
  white: "white",
  "mylife-green": "mylife",
  dexcom: "dexcom",
  minimed: "minimed",
};

const sizeMap: Record<string, ButtonSize> = {
  sm: "sm",
  lg: "lg",
  default: "sm",
};

type ButtonProps = {
  blok: ButtonStoryblok;
} & ComponentProps<"button">;

const Button = ({ blok }: ButtonProps) => {
  if (blok.button_type === "login_modal") {
    return <AuthModalButton blok={blok} />;
  }
  if (blok.button_type === "membership_payment" && process.env.NEXT_PUBLIC_CHECKOUT_ENABLED === "true") {
    return <MembershipPaymentButton blok={blok} />;
  }
  if (blok.button_type === "praxis_contact") {
    return <PraxisContactButton blok={blok} />;
  }

  const href = getUrlWithAnchor(blok?.link) || "#";

  const mappedVariant: ButtonVariant = variantMap[blok.variant || ""] ?? "black";
  const mappedSize: ButtonSize = sizeMap[blok.size || ""] ?? "sm";

  return (
    <Link
      href={href || "#"}
      target={blok.link?.target || "_self"}
      className="cursor-pointer"
      aria-label="Mehr erfahren"
      // Server-Komponente, daher kein onClick: CtaClickTracker meldet den Klick.
      data-cta-location="content_button"
      data-cta-label={blok.label}
      {...storyblokEditable(blok)}
    >
      {blok.variant === "link" ? (
        <span className="text-button">{blok.label}</span>
      ) : (blok.variant as string) === "linkExternal" ? (
        <span className="text-button inline-flex items-center gap-1">
          {blok.label}
          <ArrowUpRight size="sm" />
        </span>
      ) : (
        <ButtonComponent
          variant={mappedVariant}
          size={mappedSize}
        >
          {blok.arrow === "left" && (
            <ArrowRight size={mappedSize === "lg" ? "lg" : "sm"} />
          )}
          {blok.label}
          {blok.arrow === "right" && (
            <ArrowRight size={mappedSize === "lg" ? "lg" : "sm"} />
          )}
        </ButtonComponent>
      )}
    </Link>
  );
};

export default Button;
